/**
 * Vorlage „Zerlassen und karamellisieren“.
 *
 * In einem kleinen Topf auf schwacher Flamme schmilzt ein Stück Butter zu
 * einer goldenen Pfütze mit feinem Schaum. Beim Karamell schmilzt ein
 * Zuckerhäufchen unter Rühren zu dunklem Sirup.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { path, roundedRectPath } from '../paths.ts';
import { compact, group, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { bubbles, steam } from '../parts/effects.ts';
import { arrow, counter } from '../parts/stage.ts';
import { GRATE_Y, stove, stoveKnob } from '../parts/stove.ts';
import { place, woodenSpoon } from '../parts/utensils.ts';
import { contents, interiorClip, pot } from '../parts/vessels.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

/** Kleiner Topf. */
const POT = { cx: 108, width: 104, height: 26 } as const;
const RIM_Y = GRATE_Y - POT.height;
/** Flamme schwach. */
const POWER = 0.3;

/** Zeitplan. */
const TIME = { melt: [0.06, 0.78], reset: [0.86, 1] } as const;

/** Inhalte. */
const ITEMS = { butter: 'Butter', sugar: 'Zucker (Karamell)' } as const;
type Item = keyof typeof ITEMS;

/** Füllstand der Schmelze: leer bis fertig (0 = randvoll, 1 = Boden). */
const LEVEL = { empty: 0.97, full: 0.45 } as const;

/** Vorlage „Zerlassen und karamellisieren“. */
export const stoveMeltTemplate = defineTemplate({
  id: 'stove.melt',
  group: 'stove',
  title: 'Zerlassen und karamellisieren',
  description:
    'Butter schmilzt bei schwacher Hitze im kleinen Topf, ohne braun zu werden. Zucker schmilzt unter Rühren zu goldbraunem Karamell.',
  durationSeconds: 6,
  keywords: [
    'zerlassen',
    'butter zerlassen',
    'zerlassene butter',
    'butter schmelzen',
    'flüssige butter',
    'karamellisieren',
    'karamell',
    'zucker schmelzen',
    'kleinen topf',
    'braune butter',
  ],
  stillTime: 0.45,
  parameters: [choice('item', 'Inhalt', ITEMS, 'butter')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (/karamel|zucker/.test(lower)) return { item: 'sugar' };
    if (lower.includes('butter')) return { item: 'butter' };
    return {};
  },
  scene(values, t) {
    const item = textValue(values, 'item', 'butter') as Item;
    const vessel = pot(POT.cx, RIM_Y, POT.width, POT.height);
    const burner = stove(POT.cx, POWER, t);
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const melt = ease.inOutSine(segment(t, ...TIME.melt)) * (1 - reset);
    const level = lerp(LEVEL.empty, LEVEL.full, melt);
    const surfaceY = RIM_Y + vessel.depth * level;
    const rx = vessel.widthAt(level);
    const bottomY = RIM_Y + vessel.depth;

    let solid: SceneNode | null = null;
    let spoon: SceneNode | null = null;
    if (item === 'butter') {
      // Das Stück wird kleiner und weicher; es sinkt in die Schmelze.
      const width = lerp(46, 8, melt);
      const height = lerp(34, 2, melt);
      solid =
        melt < 0.98
          ? shape(
              roundedRectPath(
                POT.cx - width / 2 - 4,
                bottomY - height - 2,
                width,
                height,
                lerp(3, 1, melt) + melt * 4,
              ),
              styles.solid('tint'),
            )
          : null;
    } else {
      // Zuckerhäufchen schmilzt, ein Kochlöffel rührt.
      const height = lerp(18, 0, melt);
      solid =
        height > 0.5
          ? shape(
              path()
                .move(POT.cx - 34, bottomY)
                .quad(POT.cx - 4, bottomY - height * 2, POT.cx + 26, bottomY)
                .close()
                .toString(),
              styles.snow(),
            )
          : null;
      const angle = t * 2 * Math.PI * 2;
      spoon = group(
        [
          place(
            POT.cx + Math.cos(angle) * rx * 0.4,
            surfaceY + 2 + Math.sin(angle) * 3,
            -12,
            woodenSpoon(),
            0.7,
          ),
        ],
        { clip: interiorClip(vessel, 120) },
      );
    }

    const liquidTone = item === 'butter' ? 'shade' : 'deep';
    const pool =
      melt > 0.02
        ? contents(vessel, level, liquidTone, [
            ...bubbles(
              POT.cx - rx,
              surfaceY - 6,
              rx * 2,
              8,
              item === 'butter' ? 0.25 * melt : 0.5 * melt,
              t,
              9,
            ),
          ])
        : null;

    return compact([
      ...counter(),
      ...burner.back,
      ...vessel.back,
      pool,
      solid ? group([solid], { clip: interiorClip(vessel, 30) }) : null,
      spoon,
      ...vessel.front,
      ...burner.front,
      stoveKnob(214, POWER),
      ...(item === 'sugar'
        ? steam(POT.cx, RIM_Y - 8, t, { strength: melt * 0.8, count: 3, spread: 18 })
        : []),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: POT.cx - 36, y: RIM_Y - 24 },
        { x: POT.cx - 10, y: RIM_Y - 6 },
      ]),
    ];
  },
});
